import { useEffect, useRef } from 'react';
import { unstable_batchedUpdates } from 'react-dom';
import { Subject } from 'rxjs';
import { createTimerObservable } from './observables';
import { TIMER_TIMESTAMPS } from './constants';
import { restartGameTimerMarks } from './gameTimerMarks';
import { formatTimeHMS, formatTimeAMPM } from './utils';
import { getLocalAccountSession } from '@/utilities/getters/sessionContextGetters.ts';
import { getSession } from '@/unified-modules/account/account-library';
import {
  resetGameTime,
  setCurrentTime,
  setGameStartValue,
  setGameTime,
  setSessionStartValue,
  setSessionTime,
} from '@/store/actions.ts';
import { useShallow } from 'zustand/react/shallow';
import { useStore } from '@/store/useStore';

const toSessionStartMs = (
  value: Date | string | number | null | undefined,
): number | undefined => {
  if (value == null) {
    return undefined;
  }

  const ms =
    value instanceof Date ? value.valueOf() : new Date(String(value)).valueOf();

  return Number.isNaN(ms) ? undefined : ms;
};

/** UM store first (Xsell cookie blob has no created), then refreshable blob, then server-time fallback. */
const resolveSessionStartMs = (): number | undefined => {
  const state = useStore.getState();
  const fromStore =
    toSessionStartMs(state.userSessionContext.created) ??
    toSessionStartMs(state.userSessionContext.authToken?.created);

  if (fromStore !== undefined) {
    return fromStore;
  }

  const session = getLocalAccountSession?.();
  if (session) {
    const createdStr =
      session.authToken?.created ?? session.loginToken?.created;
    const fromBlob = toSessionStartMs(createdStr ?? null);
    if (fromBlob !== undefined) {
      return fromBlob;
    }
  }

  return state.sessionStartValue;
};

/**
 * Hook to initialize and manage timers
 * @param isInitialized Flag indicating if the component is initialized
 * @returns Object containing timer state and functions
 */
export const useTimers = (isInitialized: boolean = true) => {
  const {
    serverTimeValue,
    sessionStartValue,
    currentTime: currentTimeStored,
    sessionTime: sessionTimeStored,
    gameTime: gameTimeStored,
  } = useStore(
    useShallow((s) => ({
      serverTimeValue: s.serverTimeValue,
      sessionStartValue: s.sessionStartValue,
      currentTime: s.currentTime,
      sessionTime: s.sessionTime,
      gameTime: s.gameTime,
      gameStartValue: s.gameStartValue,
    })),
  );

  // Reset the game timer whenever the user switches games. `gameResetRevision`
  // increments on every game switch / soft reload; the GAME_MARK is cleared so
  // game time restarts from zero.
  const gameResetRevision = useStore((s) => s.gameResetRevision);
  const hasActiveSession = useStore(
    (s) => s.userSessionContext.hasActiveSession,
  );
  const sessionResetRevision = useStore((s) => s.sessionResetRevision);
  const prevGameResetRevisionRef = useRef(gameResetRevision);
  const prevHasActiveSessionRef = useRef(hasActiveSession);
  const wasInitializedRef = useRef(false);

  useEffect(() => {
    if (!isInitialized) {
      wasInitializedRef.current = false;
      return;
    }

    const justInitialized = !wasInitializedRef.current;
    wasInitializedRef.current = true;

    const revisionChanged =
      prevGameResetRevisionRef.current !== gameResetRevision;
    const sessionBecameActive =
      !prevHasActiveSessionRef.current && hasActiveSession;

    prevGameResetRevisionRef.current = gameResetRevision;
    prevHasActiveSessionRef.current = hasActiveSession;

    if (!revisionChanged && !sessionBecameActive && !justInitialized) {
      return;
    }

    if (hasActiveSession) {
      resetGameTime();
      setGameStartValue(Date.now());
    }

    restartGameTimerMarks();
  }, [gameResetRevision, hasActiveSession, isInitialized]);

  // Xsell (and other native paths) seed Fabric cookies without created timestamps.
  // After Account UM hydrates, session.created lives on userSessionContext; if timers
  // start before that, ask UM once so session duration can fall back to sessionStartValue.
  useEffect(() => {
    if (!isInitialized || !hasActiveSession) {
      return;
    }

    if (resolveSessionStartMs() !== undefined) {
      return;
    }

    let cancelled = false;

    void (async () => {
      const session = await getSession();
      if (cancelled || !session?.created) {
        return;
      }

      const startMs = toSessionStartMs(session.created);
      if (startMs === undefined) {
        return;
      }

      if (useStore.getState().sessionStartValue === undefined) {
        setSessionStartValue(startMs);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [isInitialized, hasActiveSession, sessionResetRevision]);

  // Set up timer observable
  useEffect(() => {
    if (!isInitialized) {
      return;
    }

    const destroy$ = new Subject<void>();
    const timer$ = createTimerObservable(destroy$);

    // Subscribe to the timer observable
    const subscription = timer$.subscribe(() => {
      // Prepare new formatted values (undefined means "no update")
      let newCurrentTime: string | undefined;
      let newSessionTime: string | undefined;
      let newGameTime: string | undefined;

      // Update current time calculation
      if (
        globalThis?.performance?.getEntriesByName?.(
          TIMER_TIMESTAMPS.SERVER_MARK,
        )?.length > 0 &&
        serverTimeValue
      ) {
        globalThis?.performance?.clearMeasures?.(
          TIMER_TIMESTAMPS.CURRENT_MEASURE,
        );
        globalThis?.performance?.measure?.(
          TIMER_TIMESTAMPS.CURRENT_MEASURE,
          TIMER_TIMESTAMPS.SERVER_MARK,
        );

        const timeElapsed =
          globalThis?.performance?.getEntriesByName?.(
            TIMER_TIMESTAMPS.CURRENT_MEASURE,
          )?.[0]?.duration || 0;

        const timeNow = serverTimeValue + timeElapsed;
        newCurrentTime = formatTimeAMPM(timeNow);
      }

      // Update session time
      const start = resolveSessionStartMs();
      if (start) {
        const now = Date.now();
        const sessionDuration = Math.max(0, now - start);
        newSessionTime = formatTimeHMS(sessionDuration);
      }

      // Update game time
      if (
        globalThis?.performance?.getEntriesByName?.(TIMER_TIMESTAMPS.GAME_MARK)
          ?.length > 0
      ) {
        globalThis?.performance?.clearMeasures?.(TIMER_TIMESTAMPS.GAME_MEASURE);
        globalThis?.performance?.measure?.(
          TIMER_TIMESTAMPS.GAME_MEASURE,
          TIMER_TIMESTAMPS.GAME_MARK,
        );

        const timeElapsed =
          globalThis?.performance?.getEntriesByName?.(
            TIMER_TIMESTAMPS.GAME_MEASURE,
          )?.[0]?.duration || 0;
        newGameTime = formatTimeHMS(timeElapsed);
      }

      // Apply updates in a single batch and only when values changed
      unstable_batchedUpdates(() => {
        if (
          newCurrentTime !== undefined &&
          newCurrentTime !== currentTimeStored
        ) {
          setCurrentTime(newCurrentTime);
        }

        if (
          newSessionTime !== undefined &&
          newSessionTime !== sessionTimeStored
        ) {
          setSessionTime(newSessionTime);
        }

        if (newGameTime !== undefined && newGameTime !== gameTimeStored) {
          setGameTime(newGameTime);
        }
      });
    });

    return () => {
      // Unsubscribe from the timer observable
      subscription.unsubscribe();
      destroy$.next();
      destroy$.complete();
      globalThis?.performance?.clearMeasures?.(
        TIMER_TIMESTAMPS.CURRENT_MEASURE,
      );
      globalThis?.performance?.clearMeasures?.(
        TIMER_TIMESTAMPS.SESSION_MEASURE,
      );
      globalThis?.performance?.clearMeasures?.(TIMER_TIMESTAMPS.GAME_MEASURE);
    };
  }, [
    currentTimeStored,
    gameTimeStored,
    isInitialized,
    serverTimeValue,
    sessionStartValue,
    sessionTimeStored,
  ]);
};
